import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProject } from "../../api";
import { Project } from "../../types";

const ProjectDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      getProject(parseInt(id, 10))
        .then((res) => {
          setProject(res.data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to fetch project:", err);
          setLoading(false);
        });
    }
  }, [id]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!project) {
    return <div>Project not found.</div>;
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "ongoing":
        return "bg-yellow-100 text-yellow-800";
      case "planning":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-red-100 text-red-800";
    }
  };

  return (
    <div className="bg-white py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            to="/projects"
            className="text-indigo-600 hover:text-indigo-800"
          >
            ← Back to Projects
          </Link>
        </div>

        <div className="mb-8">
          <div className="mb-4 flex items-center justify-between">
            <h1 className="text-4xl font-bold text-gray-900">
              {project.title}
            </h1>
            <span
              className={`rounded-full px-4 py-2 text-sm font-semibold ${getStatusClass(
                project.status
              )}`}
            >
              {project.status}
            </span>
          </div>

          <div className="flex flex-wrap gap-4 text-gray-600">
            {project.location && (
              <div className="flex items-center">
                <span className="mr-2">📍</span>
                <span>{project.location}</span>
              </div>
            )}
            {project.budget && (
              <div className="flex items-center">
                <span className="mr-2">💰</span>
                <span>
                  Budget: ₦
                  {Number(project.budget).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
            {project.start_date && (
              <div className="flex items-center">
                <span className="mr-2">📅</span>
                <span>
                  Started:{" "}
                  {new Date(project.start_date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
            )}
            {project.completion_date && (
              <div className="flex items-center">
                <span className="mr-2">✓</span>
                <span>
                  Completed:{" "}
                  {new Date(project.completion_date).toLocaleDateString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }
                  )}
                </span>
              </div>
            )}
          </div>
        </div>

        {project.featured_image && (
          <div className="mb-8 overflow-hidden rounded-lg shadow-lg">
            <img
              src={`/storage/${project.featured_image}`}
              alt={project.title}
              className="h-96 w-full object-cover"
            />
          </div>
        )}

        <div className="mb-8 rounded-lg bg-gray-50 p-8">
          <h2 className="mb-4 text-2xl font-bold text-gray-900">
            Project Description
          </h2>
          <div
            className="prose max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: project.description }}
          />
        </div>

        {project.images && project.images.length > 0 && (
          <div className="mb-8">
            <h2 className="mb-6 text-2xl font-bold text-gray-900">
              Project Gallery
            </h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {project.images.map((image) => (
                <div
                  key={image.id}
                  className="overflow-hidden rounded-lg bg-white shadow-md transition hover:shadow-xl"
                >
                  <img
                    src={`/storage/${image.image_path}`}
                    alt={image.caption || project.title}
                    className="h-64 w-full cursor-pointer object-cover"
                  />
                  {image.caption && (
                    <div className="bg-gray-50 p-3">
                      <p className="text-sm text-gray-600">{image.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetails;
